import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createLazyLoadSlice from './createLazyLoadSlice';
import API_ENDPOINTS from '@/api/apiConfig';
import { apiUrl } from '@/api/axios.js';
import { handleResponseError } from '@/core/utils/handleResponseError.js';
import {
    NormalizedTestItem,
    TestItem,
    TestsFilterId,
    TESTS_FILTER_SLUG,
} from '@/store/types/testItemTypes';
import { MyTestsState } from '@/store/types/myTestsTypes';

/**
 * Cross-slice action: меняет признак «в избранном» для теста по id.
 *
 * Любой слайс, где лежит `NormalizedTestItem` (сейчас — `myTests` и
 * `resultRecommendations`), слушает этот action в extraReducers и
 * обновляет своё отражение. Так карточка теста синхронизируется между
 * вкладкой «Мои тесты» и «Рекомендациями» — дернули сердечко в одном
 * месте, и в другом оно тоже мгновенно рассинхронизируется.
 */
export const testFavoriteSet = createAction<{ id: number; isFavorite: boolean }>(
    'favorites/testFavoriteSet',
);

// ──────────────────────────────────────────────────────────────────────────
// «Мои тесты» — список на вкладке `profile#tests`.
//
// Слайс хранит UI-ready форму (NormalizedTestItem) + активный фильтр
// `TestsFilterId`. Нормализация snake→camel и enum status живёт здесь же,
// компоненты читают готовые camelCase-поля через useLazyLoad / селекторы.
// ──────────────────────────────────────────────────────────────────────────

const myTestsSlice = createLazyLoadSlice<NormalizedTestItem>('myTests', {});

const mapStatus = (s: TestItem['status']): NormalizedTestItem['status'] => {
    if (s === 'passed') return 'passed';
    if (s === 'in_progress') return 'inProgress';
    return 'none';
};

const normalizeTestItem = (item: TestItem): NormalizedTestItem => ({
    id: item.id,
    url: item.url,
    name: item.name,
    previewPicture: item.preview_picture,
    isPro: item.is_pro ?? false,
    isFavorite: item.is_favorite ?? false,
    status: mapStatus(item.status ?? null),
});

const ZERO_COUNTS: Record<TestsFilterId, number> = {
    all: 0,
    favorite: 0,
    passed: 0,
    inProgress: 0,
};

/** Счётчики приходят snake_case (контракт API) — приводим к id фильтров. */
const normalizeCounts = (raw: Record<string, number> | undefined): Record<TestsFilterId, number> =>
    raw
        ? {
            all: raw.all ?? 0,
            favorite: raw.favorite ?? 0,
            passed: raw.passed ?? 0,
            inProgress: raw.in_progress ?? 0,
        }
        : ZERO_COUNTS;

const initialState: MyTestsState = {
    ...myTestsSlice.getInitialState(),
    filter: 'all',
    counts: ZERO_COUNTS,
};

const combinedSlice = createSlice({
    name: 'myTests',
    initialState,
    reducers: {
        ...myTestsSlice.caseReducers,

        /**
         * Положить в стор UI-shape (нормализованный `NormalizedTestItem[]`)
         * из wire-shape `TestItem[]`.
         *
         * Дедуп по `id`, а не по ссылке: `useLazyLoad` триггерит
         * `fetchMyTestsPage(currentPage)` на КАЖДОМ монтировании, а
         * `ProfileTabs` размонтирует содержимое неактивной вкладки
         * (см. `Tabs.tsx` — рендерит только `activeItem.content`).
         * При возврате на вкладку `tests` эффект фиксируется снова,
         * приходит тот же набор — без дедупа мы удваивали `state.data`,
         * и после этого любой фильтр показывал «удвоенный» список
         * вместо фильтрованного.
         *
         * Регрессия покрыта `myTestsSlice.test.ts`
         * («не дублирует элементы при повторном setDataLoaded»).
         */
        setDataLoaded(state, action: PayloadAction<TestItem[]>) {
            const normalized = action.payload.map(normalizeTestItem);
            const seenIds = new Set(state.data.map((item) => item.id));
            const fresh = normalized.filter((item) => !seenIds.has(item.id));
            state.data = [...state.data, ...fresh];
            state.isFetching = false;
        },

        setCounts(state, action: PayloadAction<Record<string, number> | undefined>) {
            state.counts = normalizeCounts(action.payload);
        },

        /**
         * Смена фильтра — это НОВАЯ выборка на сервере, а не другой взгляд на
         * уже загруженную. Сбрасываем список и пагинацию, иначе к страницам
         * «Избранного» подмешались бы страницы предыдущего фильтра, а
         * `lastPage` остался бы от него же.
         *
         * `counts` не трогаем: они описывают всю выборку и от фильтра не зависят.
         */
        setMyTestsFilter(state, action: PayloadAction<TestsFilterId>) {
            if (state.filter === action.payload) {
                return;
            }
            state.filter = action.payload;
            state.data = [];
            state.currentPage = 1;
            state.lastPage = 1;
            state.isLastPageSet = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(testFavoriteSet, (state, action) => {
            const { id, isFavorite } = action.payload;
            const item = state.data.find((t) => t.id === id);
            if (!item || item.isFavorite === isFavorite) {
                return;
            }

            item.isFavorite = isFavorite;
            state.counts.favorite += isFavorite ? 1 : -1;

            // Под фильтром «Избранное» список — это выборка сервера, и снятое
            // сердечко из неё выпадает. Раньше это делала клиентская
            // фильтрация; теперь убираем элемент сами, иначе он остался бы
            // висеть в списке избранного с пустым сердцем.
            if (!isFavorite && state.filter === 'favorite') {
                state.data = state.data.filter((t) => t.id !== id);
            }
        });
    },
});

export const {
    incrementPage,
    fetchDataStart,
    setDataLoaded,
    setLastPage,
    fetchDataError,
    setCounts,
    setMyTestsFilter,
} = combinedSlice.actions;

/**
 * Загрузка страницы «Мои тесты» с бэкенда.
 * GET /api/profile/my-tests?page={n}&filter={slug}
 *
 * Свой thunk, а не общий `getTestListData`, по двум причинам:
 *   • фильтр берётся из стора и уходит в query — сервер отдаёт уже
 *     отфильтрованную страницу (см. `MyTestsService::getPageForUser`);
 *   • `last_page` перечитывается на КАЖДОМ ответе. `getTestListData`
 *     запоминает его один раз (`isLastPageSet`), а у нас он меняется вместе
 *     с фильтром: 13 страниц во «Всех» против 1 в «Избранном».
 */
export const fetchMyTestsPage = (page: number) => async (dispatch, getState) => {
    const { filter } = getState().myTests as MyTestsState;

    dispatch(combinedSlice.actions.fetchDataStart());
    try {
        const params = new URLSearchParams({ page: String(page) });
        if (filter !== 'all') {
            params.set('filter', TESTS_FILTER_SLUG[filter]);
        }

        const { data } = await apiUrl.get(`${API_ENDPOINTS.myTests}?${params}`, {
            withCredentials: true,
        });

        // Ответ мог прийти от предыдущего фильтра (пользователь успел
        // переключиться, пока запрос летел) — такой результат не наш.
        const { filter: currentFilter } = getState().myTests as MyTestsState;
        if (currentFilter !== filter) {
            return;
        }

        dispatch(combinedSlice.actions.setDataLoaded(data.data));
        dispatch(combinedSlice.actions.setLastPage(data.last_page));
        dispatch(combinedSlice.actions.setCounts(data.counts));
    } catch (error) {
        handleResponseError(error, (message: string) =>
            dispatch(combinedSlice.actions.fetchDataError(message)));
    }
};

/**
 * Убрать тест из избранного.
 *
 * Optimistic: сразу выставляем `isFavorite=false` (через cross-slice
 * `testFavoriteSet`), затем делаем DELETE-запрос. При ошибке — откатываем
 * флаг обратно на `true`.
 */
export const removeTestFromFavorites = (id: number) => async (dispatch) => {
    dispatch(testFavoriteSet({ id, isFavorite: false }));
    try {
        await apiUrl.delete(`${API_ENDPOINTS.removeFavoriteTest}${id}`);
    } catch {
        dispatch(testFavoriteSet({ id, isFavorite: true }));
    }
};

/**
 * Добавить тест в избранное.
 *
 * Optimistic: сразу выставляем `isFavorite=true`, затем делаем POST-запрос.
 * При ошибке — откатываем флаг обратно на `false`.
 */
export const addTestToFavorites = (id: number) => async (dispatch) => {
    dispatch(testFavoriteSet({ id, isFavorite: true }));
    try {
        await apiUrl.post(`${API_ENDPOINTS.addFavoriteTest}${id}`);
    } catch {
        dispatch(testFavoriteSet({ id, isFavorite: false }));
    }
};

export default combinedSlice.reducer;
export { combinedSlice as myTestsSlice };
